import React from 'react';

import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import type { Industry } from '../types';

interface IndustryCardProps {
  industry: Industry;
  onLearnMore: (industryId: string) => void;
}

const IndustryCard: React.FC<IndustryCardProps> = ({ industry, onLearnMore }) => {
  return (
    <div className="bg-card rounded-lg shadow-md border border-border overflow-hidden hover:shadow-lg transition-all duration-fast group">
      <div className="relative h-48 overflow-hidden">
        <Image
          src={industry.image}
          alt={industry.alt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-fast"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div className="absolute bottom-4 left-4 flex items-center space-x-3">
          <div className="flex items-center justify-center w-12 h-12 bg-white rounded-lg">
            <Icon name={industry.icon} size={24} color="var(--color-primary)" />
          </div>
          <h3 className="text-xl font-bold text-white">{industry.name}</h3>
        </div>
      </div>

      <div className="p-6">
        <p className="text-text-secondary mb-4 line-clamp-2">{industry.description}</p>

        <div className="space-y-3 mb-6">
          <div className="flex items-start space-x-2">
            <Icon name="Clock" size={16} className="text-success mt-1 flex-shrink-0" />
            <span className="text-sm text-text-secondary">
              Save {industry.metrics.timeSaved} monthly
            </span>
          </div>
          <div className="flex items-start space-x-2">
            <Icon name="TrendingDown" size={16} className="text-success mt-1 flex-shrink-0" />
            <span className="text-sm text-text-secondary">
              Reduce costs by {industry.metrics.costReduction}
            </span>
          </div>
          <div className="flex items-start space-x-2">
            <Icon name="Zap" size={16} className="text-success mt-1 flex-shrink-0" />
            <span className="text-sm text-text-secondary">
              Increase efficiency by {industry.metrics.efficiency}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {industry.tools.slice(0, 3).map((tool, index) => (
            <span
              key={index}
              className="px-3 py-1 bg-surface text-text-secondary text-xs rounded-full"
            >
              {tool}
            </span>
          ))}
          {industry.tools.length > 3 && (
            <span className="px-3 py-1 bg-surface text-text-secondary text-xs rounded-full">
              +{industry.tools.length - 3} more
            </span>
          )}
        </div>

        <Button
          variant="outline"
          fullWidth
          iconName="ArrowRight"
          iconPosition="right"
          onClick={() => onLearnMore(industry.id)}
        >
          Explore Solutions
        </Button>
      </div>
    </div>
  );
};

export default IndustryCard;